
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { UserCog, User, Briefcase, MapPin, Clock, MessageSquare } from "lucide-react";
import { PersonalInfoSection } from "./sections/PersonalInfoSection";
import { ProfessionalInfoSection } from "./sections/ProfessionalInfoSection";
import { ContactInfoSection } from "./sections/ContactInfoSection";
import { PreferencesSection } from "./sections/PreferencesSection";
import { ProfileHeader } from "./ProfileHeader";

interface ExtendedUserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  phone?: string;
  linkedin_url?: string;
  location_city?: string;
  location_state?: string;
  location_country?: string;
  timezone?: string;
  bio?: string;
  years_experience?: number;
  specializations?: string[];
  work_hours_start?: string;
  work_hours_end?: string;
  preferred_communication?: string;
}

export default function EnhancedUserProfile() {
  const [activeTab, setActiveTab] = useState("personal");

  const { data: userProfile, isLoading, refetch } = useQuery({
    queryKey: ['enhanced-user-profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error("User not found");
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      return profile as ExtendedUserProfile;
    }
  });

  const calculateCompletionPercentage = () => {
    if (!userProfile) return 0;
    
    const fields = [
      'first_name', 'last_name', 'phone', 'linkedin_url',
      'location_city', 'location_state', 'timezone', 'bio',
      'years_experience', 'specializations', 'work_hours_start',
      'work_hours_end', 'preferred_communication'
    ];
    
    const filledFields = fields.filter(field => {
      const value = userProfile[field as keyof ExtendedUserProfile];
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && value !== '';
    });
    
    return Math.round((filledFields.length / fields.length) * 100);
  };

  const handleProfileUpdate = async (fieldName: string, value: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to update your profile");
        return;
      }
      
      const { error } = await supabase
        .from('profiles')
        .update({ [fieldName]: value })
        .eq('id', user.id);
        
      if (error) throw error;
      
      toast.success("Profile updated successfully");
      refetch();
    } catch (error: any) {
      toast.error(`Failed to update profile: ${error.message}`);
    }
  };

  const handleBulkUpdate = async (updates: Partial<ExtendedUserProfile>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to update your profile");
        return;
      }
      
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);
        
      if (error) throw error;
      
      toast.success("Profile updated successfully");
      refetch();
    } catch (error: any) {
      toast.error(`Failed to update profile: ${error.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 w-full">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 w-48 bg-gray-200 rounded mb-6"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="p-6 w-full">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="p-6">
              <p>Profile not found. Please try refreshing the page.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const completionPercentage = calculateCompletionPercentage();

  return (
    <div className="p-6 w-full">
      <h1 className="text-3xl font-bold mb-6">My Profile</h1>
      
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <UserCog className="h-5 w-5" />
                Profile Completion
              </CardTitle>
              <span className="text-sm font-medium">{completionPercentage}%</span>
            </div>
            <Progress value={completionPercentage} className="w-full" />
          </CardHeader>
        </Card>

        <ProfileHeader 
          userProfile={userProfile} 
          onAvatarUpdate={(url) => handleProfileUpdate('avatar_url', url)}
        />

        <Card>
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="personal" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Personal
                </TabsTrigger>
                <TabsTrigger value="professional" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Professional
                </TabsTrigger>
                <TabsTrigger value="contact" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Contact & Location
                </TabsTrigger>
                <TabsTrigger value="preferences" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Preferences
                </TabsTrigger>
              </TabsList>

              <div className="p-6">
                <TabsContent value="personal" className="mt-0">
                  <PersonalInfoSection 
                    userProfile={userProfile}
                    onUpdate={handleBulkUpdate}
                  />
                </TabsContent>

                <TabsContent value="professional" className="mt-0">
                  <ProfessionalInfoSection 
                    userProfile={userProfile}
                    onUpdate={handleBulkUpdate}
                  />
                </TabsContent>

                <TabsContent value="contact" className="mt-0">
                  <ContactInfoSection 
                    userProfile={userProfile}
                    onUpdate={handleBulkUpdate}
                  />
                </TabsContent>

                <TabsContent value="preferences" className="mt-0">
                  <PreferencesSection 
                    userProfile={userProfile}
                    onUpdate={handleBulkUpdate}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
